import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STYLES = [
  "Abstract Expressionist",
  "Atmospheric Landscape",
  "Mixed Organic Texture",
];

const inputCls =
  "w-full bg-ground border border-line-light px-3 py-2 text-sm text-white focus:outline-none focus:border-gold";
const labelCls = "block text-xs mono-tag uppercase text-slate-400 mb-1";

const STEPS = [
  {
    title: "Palette & Space Brief",
    desc: "Share your wall specs, lighting, and mood references.",
  },
  {
    title: "Color Study Review",
    desc: "Receive scale digital sketches before canvas work begins.",
  },
  {
    title: "Crated & Delivered",
    desc: "Archival varnishing with custom crate delivery.",
  },
];

export default function Commissions() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    style: STYLES[0],
    dimensions: "",
    budget: "",
    timeline: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/commissions", form);
      setSent(true);
      toast.success("Commission request received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="commissions" className="py-24 border-b border-line bg-ground-alt">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="mono-tag text-xs uppercase text-gold tracking-widest block mb-2">
              Tailored Work
            </span>
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              Commission a Piece
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              For collectors, interior designers, and corporate spaces seeking
              custom dimensions, tone palettes, and emotional weight.
            </p>
            <div className="space-y-4 pt-4 border-t border-line">
              {STEPS.map((s) => (
                <div key={s.title} className="flex items-start gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-white uppercase">{s.title}</h4>
                    <p className="text-xs text-slate-400">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 bg-ground-elevated p-8 border border-line-light">
            <h3 className="text-lg font-bold uppercase text-white mb-6">
              Commission Enquiry Form
            </h3>
            {sent ? (
              <div
                className="p-8 border border-gold text-center"
                data-testid="commission-success"
              >
                <p className="text-gold mono-tag text-xs uppercase tracking-widest mb-3">
                  Request Received
                </p>
                <p className="text-white text-lg font-bold uppercase mb-2">
                  Thank you, {form.name.split(" ")[0]}.
                </p>
                <p className="text-sm text-slate-400">
                  Your brief is with the studio. Expect a reply at {form.email}{" "}
                  with a color study proposal.
                </p>
                <button
                  type="button"
                  className="mt-6 px-5 py-2.5 border border-line-light text-xs font-bold uppercase text-white hover:border-gold hover:text-gold transition-colors"
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", style: STYLES[0], dimensions: "", budget: "", timeline: "", details: "" });
                  }}
                  data-testid="commission-another-btn"
                >
                  Send Another Request
                </button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={submit} data-testid="commission-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="c-name">Your Name</label>
                    <input
                      id="c-name"
                      data-testid="commission-name-input"
                      type="text"
                      className={inputCls}
                      placeholder="Collector / Designer Name"
                      value={form.name}
                      onChange={set("name")}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="c-email">Contact Email</label>
                    <input
                      id="c-email"
                      data-testid="commission-email-input"
                      type="email"
                      className={inputCls}
                      placeholder="name@domain.com"
                      value={form.email}
                      onChange={set("email")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="c-style">Style Preference</label>
                    <select
                      id="c-style"
                      data-testid="commission-style-select"
                      className={inputCls}
                      value={form.style}
                      onChange={set("style")}
                    >
                      {STYLES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="c-dimensions">Target Dimensions</label>
                    <input
                      id="c-dimensions"
                      data-testid="commission-dimensions-input"
                      type="text"
                      className={inputCls}
                      placeholder="e.g. 72 x 48 inches"
                      value={form.dimensions}
                      onChange={set("dimensions")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="c-budget">Budget Range</label>
                    <input
                      id="c-budget"
                      data-testid="commission-budget-input"
                      type="text"
                      className={inputCls}
                      placeholder="e.g. $2,000 - $5,000"
                      value={form.budget}
                      onChange={set("budget")}
                    />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="c-timeline">Timeline / Install Date</label>
                    <input
                      id="c-timeline"
                      data-testid="commission-timeline-input"
                      type="text"
                      className={inputCls}
                      placeholder="e.g. 4-6 weeks"
                      value={form.timeline}
                      onChange={set("timeline")}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="c-details">Project Details &amp; Space Notes</label>
                  <textarea
                    id="c-details"
                    data-testid="commission-details-input"
                    rows="3"
                    className={inputCls}
                    placeholder="Describe the room, color palette, or milestone deadline..."
                    value={form.details}
                    onChange={set("details")}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold-hover transition-colors disabled:opacity-60"
                  data-testid="commission-submit-button"
                >
                  {submitting ? "Sending…" : "Submit Commission Request"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
